import { Context } from '../context/Context';
import { Texture } from './Texture';

/**
 * Options controlling texture-array upload format + filtering.
 */
export type ArrayTextureOptions = {
  /** If true, upload as SRGB8_ALPHA8 (gamma-decoded on sample). Use for albedo maps. */
  srgb?: boolean;
  /** Generate mipmaps after upload. Defaults to true. */
  mipmaps?: boolean;
};

/**
 * Wraps a WebGL2 TEXTURE_2D_ARRAY with repeat-wrap + linear filtering. All layers must
 * share the same width/height; the caller is responsible for pre-normalizing inputs.
 */
export class ArrayTexture extends Texture {
  public readonly width: number;
  public readonly height: number;
  public readonly layerCount: number;

  public override get is2d(): boolean {
    return false;
  }

  public override get is2dArray(): boolean {
    return true;
  }

  constructor(width: number, height: number, layerCount: number) {
    super();
    this.width = width;
    this.height = height;
    this.layerCount = layerCount;
  }

  /**
   * Build an ArrayTexture from per-layer RGBA byte images. Every layer must have the same
   * dimensions as (width, height). Alpha is ignored for opaque channels but still stored.
   */
  static fromLayers(
    ctx: Context,
    layers: Uint8Array[],
    width: number,
    height: number,
    options: ArrayTextureOptions = {},
  ): ArrayTexture {
    const { srgb = false, mipmaps = true } = options;
    const gl = ctx.gl;

    const tex = new ArrayTexture(width, height, layers.length);
    tex.glTexture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, tex.glTexture);

    const internalFormat = srgb ? gl.SRGB8_ALPHA8 : gl.RGBA8;
    gl.texStorage3D(
      gl.TEXTURE_2D_ARRAY,
      mipmaps ? Math.floor(Math.log2(Math.max(width, height))) + 1 : 1,
      internalFormat,
      width,
      height,
      layers.length,
    );

    for (let i = 0; i < layers.length; i++) {
      gl.texSubImage3D(
        gl.TEXTURE_2D_ARRAY,
        0,
        0, 0, i,
        width, height, 1,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        layers[i],
      );
    }

    if (mipmaps) {
      gl.generateMipmap(gl.TEXTURE_2D_ARRAY);
      gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
      // Anisotropic filtering — without this the triplanar terrain shader picks an
      // isotropic mip from the max screen-space derivative, which on a near-vertical
      // wall viewed at grazing angle produces severe diagonal moiré (one triplanar
      // projection has tight derivatives, another has huge ones, the blend between
      // their differing mip levels interferes). Aniso picks rectangular mip footprints.
      const aniso =
        gl.getExtension('EXT_texture_filter_anisotropic') ??
        gl.getExtension('MOZ_EXT_texture_filter_anisotropic') ??
        gl.getExtension('WEBKIT_EXT_texture_filter_anisotropic');
      if (aniso) {
        const maxAniso = gl.getParameter(aniso.MAX_TEXTURE_MAX_ANISOTROPY_EXT);
        gl.texParameterf(gl.TEXTURE_2D_ARRAY, aniso.TEXTURE_MAX_ANISOTROPY_EXT, Math.min(16, maxAniso));
      }
    } else {
      gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    }
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_S, gl.REPEAT);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_T, gl.REPEAT);

    gl.bindTexture(gl.TEXTURE_2D_ARRAY, null);
    return tex;
  }

  /**
   * Build an ArrayTexture from an array of HTMLImageElement / ImageBitmap. All must share
   * the same size as the first. Callers are expected to ensure images are fully loaded.
   */
  static fromImages(
    ctx: Context,
    images: (HTMLImageElement | ImageBitmap)[],
    options: ArrayTextureOptions = {},
  ): ArrayTexture {
    if (images.length === 0) {
      throw new Error('ArrayTexture.fromImages: layers must be non-empty');
    }
    const first = images[0];
    const width = first.width;
    const height = first.height;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context2d = canvas.getContext('2d', { willReadFrequently: true })!;

    const layers = images.map((img) => {
      if (img.width !== width || img.height !== height) {
        throw new Error(
          `ArrayTexture.fromImages: all layers must share dimensions; ` +
            `got ${img.width}x${img.height}, expected ${width}x${height}`,
        );
      }
      context2d.clearRect(0, 0, width, height);
      context2d.drawImage(img as CanvasImageSource, 0, 0);
      const data = context2d.getImageData(0, 0, width, height).data;
      return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
    });

    return ArrayTexture.fromLayers(ctx, layers, width, height, options);
  }
}
